import React, { type ComponentProps, useMemo } from 'react';
import { Platform, ScrollView, StyleSheet, type ViewStyle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Aside } from '@expo/html-elements';
import { theme, useResponsive } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationDetailsContainer = (
  props: CustomizationDetailsContainerProps,
) => {
  const { children, style, contentContainerStyle, ...restProps } = props;

  const { match } = useResponsive();
  const { top: safeAreaTopInset } = useSafeAreaInsets();

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerDynamicStylesXS = useMemo<ViewStyle>(() => {
    const shouldUseSafeArea = Platform.OS === 'android';

    return {
      maxHeight: (shouldUseSafeArea ? safeAreaTopInset : 0) + 275,
    };
  }, [safeAreaTopInset]);

  const outerContainerStyles = [match([undefined, styles.outerContainerSM])];
  const containerStyles = [
    styles.container,
    match([[styles.containerXS, containerDynamicStylesXS], styles.containerSM]),
    style,
  ];
  const contentContainerStyles = [
    styles.contentContainer,
    contentContainerStyle,
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Aside style={outerContainerStyles}>
      <ScrollView
        style={containerStyles}
        contentContainerStyle={contentContainerStyles}
        {...restProps}
      >
        {children}
      </ScrollView>
    </Aside>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  outerContainerSM: {
    width: '100%',
    maxWidth: 400,
  },
  container: {
    flexGrow: 1,
    backgroundColor: theme.colors.CREAM,
  },
  containerXS: {
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.DARK_KALE,
  },
  containerSM: {
    paddingTop: theme.spacing['1'],
    borderRightWidth: 1,
    borderRightColor: theme.colors.DARK_KALE,
  },
  contentContainer: {
    flexGrow: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type CustomizationDetailsContainerProps = ComponentProps<typeof ScrollView>;
